import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { 
  AudioWaveform, 
  Mic, 
  CloudLightning, 
  Rocket, 
  MessageSquare, 
  Network, 
  Sparkles, 
  BrainCircuit, 
  Stars, 
  Search, 
  Cloud, 
  Server, 
  Flame, 
  Activity, 
  Database 
} from "lucide-react";

export type MissionWordHeroProps = {
  items?: string[];
  theme?: "dark" | "light" | "system";
  hue?: number;
  startVh?: number;
  spaceVh?: number;
};

export const MissionWordHero = ({
  items = [
    "Ethical GenAI.",
    "Zero-Trust Security.",
    "Deep Tech Research.",
    "Community Impact.",
    "Next-Gen Architectures.",
    "Secure Digital Realities."
  ],
  hue = 43, // Brand Gold Hue
  startVh = 50,
  spaceVh = 60,
}: MissionWordHeroProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeWordIdx, setActiveWordIdx] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveWordIdx((prev) => (prev + 1) % items.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [items.length]);

  useEffect(() => {
    const root = containerRef.current;
    if (root) {
      root.style.setProperty("--hue", String(hue));
      root.style.setProperty("--start", `${startVh}vh`);
      root.style.setProperty("--space", `${spaceVh}vh`);
      root.style.setProperty("--count", String(items.length));
    }
  }, [hue, startVh, spaceVh, items.length]);

  // Pause the marquee rows while the user is actively scrolling and while
  // the tab is hidden: the two large marquee strips are the most expensive
  // continuously-animating content on this section, and they can't be seen
  // mid-scroll or in a backgrounded tab. Resume ~150ms after scrolling stops
  // (transform-position based, so resuming has no visual jump).
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const applyPause = (paused: boolean) => {
      root
        .querySelectorAll<HTMLElement>(".animate-marquee, .animate-marquee-reverse")
        .forEach((el) => {
          el.style.animationPlayState = paused ? "paused" : "";
        });
    };

    let idleTimer: number | undefined;
    const onScroll = () => {
      applyPause(true);
      window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(() => applyPause(false), 150);
    };
    const onVisibility = () => applyPause(document.hidden);

    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearTimeout(idleTimer);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-visible bg-[#030712]"
      style={{
        "--count": items.length,
        "--start": `${startVh}vh`,
        "--space": `${spaceVh}vh`,
        "--hue": hue,
      } as React.CSSProperties}
    >
      {/* Mobile Word Cycling Header (Responsive fallback for touch/smaller viewports) */}
      <div className="block lg:hidden w-full font-black text-center py-20 px-4">
        <h2 className="text-3xl sm:text-4xl tracking-tight text-white leading-normal font-black">
          we architect <br />
          <span className="font-black inline-block mt-2 text-[#efae00]">
            {items[activeWordIdx]}
          </span>
        </h2>
      </div>

      {/* Desktop Sticky Word Cycling Header */}
      <div className="hidden lg:block">
        <header className="ew-word-hero-header font-black">
          <section className="ew-word-hero-section px-4 sm:px-8 lg:px-16">
            <h2 className="ew-word-hero-title font-black tracking-tight text-white/95">
              <span>we architect</span>
            </h2>

            <ul className="ew-word-hero-list font-black tracking-tight">
              {items.map((word, i) => (
                <li
                  key={i}
                  style={{ "--i": i } as React.CSSProperties}
                  className="ew-word-hero-item"
                >
                  {word}
                </li>
              ))}
            </ul>
          </section>
        </header>

        {/* Spacer to allow scrolling through the list of words before card enters */}
        <div className="h-[var(--space,60vh)] w-full pointer-events-none relative z-10" />
      </div>

      {/* Sliding Content Panel */}
      <main className="relative z-20 w-full h-auto bg-[#030712] rounded-t-[2.5rem] md:rounded-t-[4rem] border-t border-white/10 py-16 sm:py-24 px-4 sm:px-8 lg:px-16 overflow-visible">
        {/* Glowing Background Accents Removed */}

        <div className="max-w-6xl mx-auto w-full relative z-10">
          {/* Header & Motto (Center-aligned & styled to match page design language) */}
          <div className="flex flex-col items-center text-center mb-section max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="inline-flex items-center justify-center gap-2.5 mb-3"
            >
              <span className="h-px w-8 bg-[#efae00]/70" />
              <span className="text-xs font-bold tracking-[0.28em] text-[#efae00] uppercase">
                EXPERTISE
              </span>
              <span className="h-px w-8 bg-[#efae00]/70" />
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-4xl font-bold text-white leading-tight tracking-tight mb-6"
            >
              We Are Expert In
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-lg text-slate-300/90 font-light leading-relaxed max-w-4xl mt-6 mx-auto"
            >
              Powering next-generation solutions with enterprise-grade AI, cloud-native architectures, and intelligent automation — the technologies driving modern digital transformation.
            </motion.p>
          </div>

          {/* Double Marquee Showcase */}
          <motion.div
            initial={{ opacity: 0, y: 25 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true, margin: "-80px" }}
            className="flex flex-col gap-6 mt-12 w-full overflow-hidden select-none"
          >
            {/* Row 1: Left moving */}
            <div className="relative flex w-full overflow-hidden [mask-image:linear-gradient(to_right,transparent_0%,black_10%,black_90%,transparent_100%)]">
              <div className="flex shrink-0 gap-6 min-w-full animate-marquee py-2">
                {[
                  { name: "Eleven Labs", icon: AudioWaveform, hoverClass: "group-hover:text-white" },
                  { name: "Unreal Speech", icon: Mic, hoverClass: "group-hover:text-blue-400" },
                  { name: "Fly.io", icon: CloudLightning, hoverClass: "group-hover:text-[#8434CE]" },
                  { name: "Railway", icon: Rocket, hoverClass: "group-hover:text-white" },
                  { name: "WhatsApp Business", icon: MessageSquare, hoverClass: "group-hover:text-[#25D366]" },
                  { name: "Meta", icon: Network, hoverClass: "group-hover:text-[#0668E1]" },
                  { name: "OpenAI", icon: Sparkles, hoverClass: "group-hover:text-[#10A37F]" },
                  { name: "Anthropic", icon: BrainCircuit, hoverClass: "group-hover:text-[#D97757]" },
                ].map((tech) => (
                  <div
                    key={tech.name}
                    className="flex items-center justify-center gap-3 px-8 py-4 h-14 sm:h-16 rounded-2xl border border-white/10 bg-[#0a0f1e]/40 backdrop-blur-md hover:border-white/20 hover:bg-[#0a0f1e]/70 transition-all duration-300 group shrink-0 relative overflow-hidden"
                  >
                    <tech.icon className={`h-6 w-6 text-slate-400 transition-colors duration-300 ${tech.hoverClass}`} />
                    <span className="text-xs sm:text-sm font-semibold text-slate-400 group-hover:text-white transition-colors duration-300">
                      {tech.name}
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex shrink-0 gap-6 min-w-full animate-marquee py-2" aria-hidden="true">
                {[
                  { name: "Eleven Labs", icon: AudioWaveform, hoverClass: "group-hover:text-white" },
                  { name: "Unreal Speech", icon: Mic, hoverClass: "group-hover:text-blue-400" },
                  { name: "Fly.io", icon: CloudLightning, hoverClass: "group-hover:text-[#8434CE]" },
                  { name: "Railway", icon: Rocket, hoverClass: "group-hover:text-white" },
                  { name: "WhatsApp Business", icon: MessageSquare, hoverClass: "group-hover:text-[#25D366]" },
                  { name: "Meta", icon: Network, hoverClass: "group-hover:text-[#0668E1]" },
                  { name: "OpenAI", icon: Sparkles, hoverClass: "group-hover:text-[#10A37F]" },
                  { name: "Anthropic", icon: BrainCircuit, hoverClass: "group-hover:text-[#D97757]" },
                ].map((tech) => (
                  <div
                    key={tech.name + "-dup"}
                    className="flex items-center justify-center gap-3 px-8 py-4 h-14 sm:h-16 rounded-2xl border border-white/10 bg-[#0a0f1e]/40 backdrop-blur-md hover:border-white/20 hover:bg-[#0a0f1e]/70 transition-all duration-300 group shrink-0 relative overflow-hidden"
                  >
                    <tech.icon className={`h-6 w-6 text-slate-400 transition-colors duration-300 ${tech.hoverClass}`} />
                    <span className="text-xs sm:text-sm font-semibold text-slate-400 group-hover:text-white transition-colors duration-300">
                      {tech.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Row 2: Right moving */}
            <div className="relative flex w-full overflow-hidden [mask-image:linear-gradient(to_right,transparent_0%,black_10%,black_90%,transparent_100%)]">
              <div className="flex shrink-0 gap-6 min-w-full animate-marquee-reverse py-2">
                {[
                  { name: "Gemini", icon: Stars, hoverClass: "group-hover:text-[#8E24AA]" },
                  { name: "DeepSeek", icon: Search, hoverClass: "group-hover:text-[#4C73FF]" },
                  { name: "AWS", icon: Cloud, hoverClass: "group-hover:text-[#FF9900]" },
                  { name: "Google Cloud", icon: Server, hoverClass: "group-hover:text-[#4285F4]" },
                  { name: "Microsoft Azure", icon: Server, hoverClass: "group-hover:text-[#0089D6]" },
                  { name: "Firebase", icon: Flame, hoverClass: "group-hover:text-[#FFCA28]" },
                  { name: "n8n", icon: Activity, hoverClass: "group-hover:text-[#EA4B71]" },
                  { name: "MongoDB", icon: Database, hoverClass: "group-hover:text-[#47A248]" },
                ].map((tech) => (
                  <div
                    key={tech.name}
                    className="flex items-center justify-center gap-3 px-8 py-4 h-14 sm:h-16 rounded-2xl border border-white/10 bg-[#0a0f1e]/40 backdrop-blur-md hover:border-white/20 hover:bg-[#0a0f1e]/70 transition-all duration-300 group shrink-0 relative overflow-hidden"
                  >
                    <tech.icon className={`h-6 w-6 text-slate-400 transition-colors duration-300 ${tech.hoverClass}`} />
                    <span className="text-xs sm:text-sm font-semibold text-slate-400 group-hover:text-white transition-colors duration-300">
                      {tech.name}
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex shrink-0 gap-6 min-w-full animate-marquee-reverse py-2" aria-hidden="true">
                {[
                  { name: "Gemini", icon: Stars, hoverClass: "group-hover:text-[#8E24AA]" },
                  { name: "DeepSeek", icon: Search, hoverClass: "group-hover:text-[#4C73FF]" },
                  { name: "AWS", icon: Cloud, hoverClass: "group-hover:text-[#FF9900]" },
                  { name: "Google Cloud", icon: Server, hoverClass: "group-hover:text-[#4285F4]" },
                  { name: "Microsoft Azure", icon: Server, hoverClass: "group-hover:text-[#0089D6]" },
                  { name: "Firebase", icon: Flame, hoverClass: "group-hover:text-[#FFCA28]" },
                  { name: "n8n", icon: Activity, hoverClass: "group-hover:text-[#EA4B71]" },
                  { name: "MongoDB", icon: Database, hoverClass: "group-hover:text-[#47A248]" },
                ].map((tech) => (
                  <div
                    key={tech.name + "-dup"}
                    className="flex items-center justify-center gap-3 px-8 py-4 h-14 sm:h-16 rounded-2xl border border-white/10 bg-[#0a0f1e]/40 backdrop-blur-md hover:border-white/20 hover:bg-[#0a0f1e]/70 transition-all duration-300 group shrink-0 relative overflow-hidden"
                  >
                    <tech.icon className={`h-6 w-6 text-slate-400 transition-colors duration-300 ${tech.hoverClass}`} />
                    <span className="text-xs sm:text-sm font-semibold text-slate-400 group-hover:text-white transition-colors duration-300">
                      {tech.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Scoped CSS styling tag */}
      <style>{`
        .ew-word-hero-header {
          position: sticky;
          --lh: 1.25em;
          top: calc((var(--count) - 1) * -1 * var(--lh));
          line-height: 1.25;
          display: flex;
          align-items: start;
          width: 100%;
          z-index: 10;
          /* Fluid size: larger on phones while still fitting side-by-side, large on desktop */
          font-size: clamp(1.15rem, 0.7rem + 2.5vw, 4.5rem);
        }

        .ew-word-hero-section {
          display: flex;
          width: 100%;
          align-items: baseline;
          justify-content: center;
          padding-top: calc(var(--start, 50vh) - 0.5 * var(--lh));
          /* Keep 'we architect' + words in a single row across all breakpoints */
          flex-wrap: nowrap;
          gap: 0.12em;
        }

        .ew-word-hero-title {
          position: sticky;
          top: calc(var(--start, 50vh) - 0.5 * var(--lh));
          margin: 0;
          font-size: inherit;
          font-weight: inherit;
          white-space: nowrap;
          flex: 0 0 auto;
          /* Make the h2 baseline align with the list's first-line baseline */
          line-height: 1.25;
        }

        .ew-word-hero-list {
          list-style: none;
          padding: 0;
          margin: 0;
          font-size: inherit;
          font-weight: inherit;
          flex: 0 1 auto;
          min-width: 0;
          text-align: left;
          line-height: 1.25;
        }

        .ew-word-hero-item {
          --dimmed: rgba(255, 255, 255, 0.08);
          --accent: hsl(var(--hue, 43) 100% 48%);
          background:
            linear-gradient(
              180deg,
              var(--dimmed) 0 calc(var(--start, 50vh) - 0.5 * var(--lh)),
              var(--accent) calc(var(--start, 50vh) - 0.55 * var(--lh)) calc(var(--start, 50vh) + 0.55 * var(--lh)),
              var(--dimmed) calc(var(--start, 50vh) + 0.5 * var(--lh))
            );
          background-attachment: fixed;
          color: transparent;
          background-clip: text;
          -webkit-background-clip: text;
          white-space: nowrap;
        }

        @keyframes marquee {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        
        .animate-marquee {
          animation: marquee 35s linear infinite;
        }

        @keyframes marquee-reverse {
          0% {
            transform: translateX(-50%);
          }
          100% {
            transform: translateX(0);
          }
        }
        
        .animate-marquee-reverse {
          animation: marquee-reverse 35s linear infinite;
        }
      `}</style>
    </div>
  );
};
